import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  CircularProgress,
  Alert,
  Link,
  TableFooter,
  TableSortLabel
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TokenIcon from './TokenIcon';
import { useTranslation } from 'react-i18next';
import { isRTL } from '../i18n';

// headCells now uses t() for translation
const useHeadCells = (t) => ([
  { id: 'coin', label: t('tables.transactions.coin', 'Token'), sortable: true },
  { id: 'type', label: t('tables.transactions.type', 'Type'), sortable: true },
  { id: 'amount', label: t('tables.transactions.amount', 'Amount'), sortable: true, align: 'right' },
  { id: 'buyPrice', label: t('tables.transactions.price', 'Buy Price'), sortable: false, align: 'right' },
  { id: 'currentPrice', label: t('tables.portfolio.currentPrice', 'Current Price'), sortable: false, align: 'right' },
  { id: 'marketValue', label: t('tables.portfolio.marketValue', 'Market Value'), sortable: false, align: 'right' },
  { id: 'date', label: t('tables.transactions.date', 'Date'), sortable: true, align: 'left' },
  { id: 'wallet', label: t('tables.wallets.label', 'Wallet'), sortable: false, align: 'left' },
  { id: 'details', label: t('tables.transactions.details', 'Details'), sortable: false, align: 'center' },
]);

const TransactionsList = ({ 
  transactions, 
  loading, 
  error, 
  onPageChange,
  currentPage = 1,
  totalPages = 1,
  itemsPerPage = 50,
  totalCount = 0,
  formatCurrency,
  prices,
  onSort,
  sortConfig,
  realOnChainBalance,
  missingAmount,
  wallets,
  onRowClick,
  onAddHoldings
}) => {
  // Add state for detailed view
  const [isDetailedView, setIsDetailedView] = useState(false);
  const { t } = useTranslation();
  const headCells = useHeadCells(t);

  // Log the request
  // console.log(`[PriceService] Request from ${caller}: ${symbols.length} symbols:`, symbols);

  // Debug log for all unique coin identifiers in transactions
  const allTxCoins = Array.from(new Set((transactions || []).map(tx => tx.coin)));

  const totalMarketValue = React.useMemo(() => {
    if (!transactions || transactions.length === 0 || !prices) {
      return 0;
    }
    return transactions.reduce((sum, tx) => {
      const priceData = prices[tx.coin];
      const price = priceData?.usd ?? 0;
      const marketValue = Math.abs(tx.amount) * price;
      return sum + marketValue;
    }, 0);
  }, [transactions, prices]);

  const totalAmount = React.useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return 0;
    }
    const total = transactions.reduce((sum, tx) => {
      const amount = Number(tx.amount);
      if (isNaN(amount)) {
        console.warn('Skipping non-numeric amount:', tx.amount, 'for transaction:', tx);
        return sum;
      }
      return sum + amount;
    }, 0);
    return total;
  }, [transactions]);

  // Compute price for on-chain balance (use first tx's price if available)
  let onChainMarketValue = null;
  if (realOnChainBalance !== null && realOnChainBalance !== undefined && transactions.length > 0) {
    const tx = transactions[0];
    const priceData = prices[tx.coin];
    const price = priceData?.usd ?? 0;
    onChainMarketValue = realOnChainBalance * price;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="textSecondary" align="center">
            No transactions found. Add a wallet to see transaction history.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getTransactionTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'receive':
      case 'buy':
        return 'success';
      case 'send':
      case 'sell':
        return 'error';
      case 'swap':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getExplorerUrl = (blockchain, txHash) => {
    if (!txHash) return '#';
    switch (blockchain) {
      case 'solana':
        return `https://solscan.io/tx/${txHash}`;
      case 'ethereum':
        return `https://etherscan.io/tx/${txHash}`;
      case 'xrp':
        return `https://xrpscan.com/tx/${txHash}`;
      case 'bitcoin':
        return `https://www.blockchain.com/btc/tx/${txHash}`;
      case 'sui':
        return `https://suiexplorer.com/txblock/${txHash}`;
      default:
        return '#';
    }
  };

  const handlePageChange = (event, page) => {
    onPageChange(page);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with Add Holdings and Detailed button */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        {/* No title here, just the action chips */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: isRTL() ? 'flex-start' : 'flex-end',
            width: '100%',
          }}
        >
          {onAddHoldings && (
            <Chip
              label={t('walletTransactions.addHoldings')}
              color="primary"
              size="small"
              variant="outlined"
              onClick={onAddHoldings}
              sx={{ fontWeight: 500, fontSize: '0.9rem', height: 32, borderRadius: '999px', display: 'flex', alignItems: 'center' }}
            />
          )}
          <Chip
            label={t('walletTransactions.detailed')}
            color="primary"
            size="small"
            variant={isDetailedView ? 'filled' : 'outlined'}
            onClick={() => setIsDetailedView(!isDetailedView)}
            sx={{ fontWeight: 500, fontSize: '0.9rem', height: 32, borderRadius: '999px', display: 'flex', alignItems: 'center' }}
          />
        </Box>
      </Box>

      {!isDetailedView ? (
        // Compact view - only token, amount, market value, and date
        <Box display="flex" flexDirection="column" gap={0} sx={{ bgcolor: '#fff', borderRadius: 1 }}>
          {/* Compact view headers */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              py: { xs: 0.5, sm: 1 }, 
              px: { xs: 0.5, sm: 2 }, 
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#fafafa',
              fontWeight: 600,
              fontSize: '0.85rem'
            }}
          >
            <Box display="flex" alignItems="center" minWidth={60} flex={2} sx={{ mr: { xs: 0.5, sm: 1 } }}>
              <span 
                onClick={() => onSort && onSort('coin')}
                style={{ cursor: onSort ? 'pointer' : 'default' }}
              >
                {t('tables.transactions.coin', 'Token')}
              </span>
            </Box>
            <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
              <span 
                onClick={() => onSort && onSort('amount')}
                style={{ cursor: onSort ? 'pointer' : 'default' }}
              >
                {t('tables.transactions.amount', 'Amount')}
              </span>
            </Box>
            <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
              {t('tables.portfolio.marketValue', 'Market Value')}
            </Box>
            <Box flex={2} minWidth={60} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <span 
                onClick={() => onSort && onSort('date')}
                style={{ cursor: onSort ? 'pointer' : 'default' }}
              >
                {t('tables.transactions.date', 'Date')}
              </span>
            </Box>
          </Box>
          
          {transactions.map((tx, index) => {
            const priceData = prices[tx.coin];
            const price = priceData?.usd ?? 0;
            const marketValue = Math.abs(tx.amount) * price;
            
            return (
              <Box 
                key={tx.id ? tx.id : `${tx.tx_hash}-${tx.coin}-${tx.amount}-${index}`} 
                onClick={() => onRowClick && onRowClick(tx.coin)}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  py: { xs: 0.5, sm: 1.5 }, 
                  px: { xs: 0.5, sm: 2 }, 
                  borderBottom: index < transactions.length - 1 ? '1px solid #f0f0f0' : 'none',
                  width: '100%', 
                  cursor: onRowClick ? 'pointer' : 'default', 
                  '&:hover': onRowClick ? { background: '#fafbfc' } : {},
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                {/* Token icon and symbol only */}
                <Box display="flex" alignItems="center" minWidth={60} flex={2} sx={{ mr: { xs: 0.5, sm: 1 }, gap: 1 }}>
                  <TokenIcon coinSymbol={tx.coin} size={22} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.1rem' }, lineHeight: 1.2 }}>{tx.coin}</Typography>
                </Box>
                
                {/* Amount */}
                <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
                  <Typography variant="subtitle2" color={tx.amount > 0 ? 'success.main' : 'error.main'} sx={{ fontWeight: 600, fontSize: { xs: '0.92rem', sm: '1rem' }, lineHeight: 1.2 }}>
                    {tx.amount > 0 ? '+' : ''}{Number(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                  </Typography>
                </Box>
                
                {/* Market Value */}
                <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.92rem', sm: '1rem' }, lineHeight: 1.2 }}>
                    {formatCurrency(marketValue)}
                  </Typography>
                </Box>
                
                {/* Date */}
                <Box flex={2} minWidth={60} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                  {(() => {
                    if (!tx.date) return <Typography variant="caption">N/A</Typography>;
                    const d = new Date(tx.date);
                    if (isNaN(d.getTime())) return <Typography variant="caption">Invalid Date</Typography>;
                    const dateStr = `${d.getFullYear()}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getDate().toString().padStart(2,'0')}`;
                    const timeStr = `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}:${d.getSeconds().toString().padStart(2,'0')}`;
                    return (
                      <>
                        <Typography variant="body2" sx={{ lineHeight: 1 }}>{dateStr}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1 }}>{timeStr}</Typography>
                      </>
                    );
                  })()}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        // Detailed view - full table with horizontal scroll
        <TableContainer component={Paper} sx={{ bgcolor: '#fff', borderRadius: 1, overflow: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.align || 'left'}
                    sortDirection={sortConfig?.sortBy === headCell.id ? sortConfig.sortOrder : false}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      position: headCell.id === 'coin' ? 'sticky' : 'static',
                      left: headCell.id === 'coin' ? 0 : 'auto',
                      zIndex: headCell.id === 'coin' ? 1 : 'auto',
                      backgroundColor: headCell.id === 'coin' ? '#fff' : 'transparent',
                      borderRight: headCell.id === 'coin' ? '1px solid #e0e0e0' : 'none',
                      whiteSpace: 'nowrap',
                      padding: '6px 10px',
                      lineHeight: 1.2
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={sortConfig?.sortBy === headCell.id}
                        direction={sortConfig?.sortBy === headCell.id ? sortConfig.sortOrder : 'asc'}
                        onClick={() => onSort && onSort(headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((tx, index) => {
                // Extract price from the correct format: { symbol: { usd: price } }
                const priceData = prices[tx.coin];
                const price = priceData?.usd ?? 0;
                const marketValue = Math.abs(tx.amount) * price;
                // Debug log for price and market value
                // console.log('TX:', tx, 'price:', price, 'marketValue:', marketValue, 'priceData:', priceData, 'prices:', prices);
                // Find wallet name/address for tooltip
                let walletName = tx.wallet_name || 'Unknown Wallet';
                let walletAddress = tx.address || '';
                if (wallets && tx.wallet_id) {
                  const walletObj = wallets.find(w => w.id === tx.wallet_id || w.address === tx.address);
                  if (walletObj) {
                    walletName = walletObj.label || walletObj.address;
                    walletAddress = walletObj.address;
                  }
                }
                // Shorten hash for display
                const shortHash = tx.tx_hash ? `${tx.tx_hash.slice(0, 6)}...${tx.tx_hash.slice(-6)}` : '';
                return (
                  <TableRow 
                    key={tx.id ? tx.id : `${tx.tx_hash}-${tx.coin}-${tx.amount}-${index}`}
                    onClick={() => onRowClick && onRowClick(tx.coin)}
                    sx={{ 
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:hover': onRowClick ? {
                        backgroundColor: 'action.hover',
                      } : {},
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: '#fff',
                        borderRight: '1px solid #e0e0e0'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <TokenIcon coinSymbol={tx.coin} size={24} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{tx.coin || 'N/A'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: { xs: 80, sm: 120 }, width: { xs: 80, sm: 120 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', p: { xs: 0.5, sm: 1 } }}>
                      <Tooltip title={tx.type || 'Unknown'} arrow>
                        <span style={{ display: 'block', width: '100%' }}>
                          <Chip 
                            label={tx.type || 'Unknown'}
                            color={getTransactionTypeColor(tx.type)}
                            size="small"
                            sx={{
                              width: '100%',
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: { xs: '0.75rem', sm: '0.85rem' },
                              p: 0,
                              '& .MuiChip-label': {
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                textAlign: 'center',
                              },
                            }}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={tx.amount > 0 ? 'success.main' : 'error.main'}>
                        {tx.amount > 0 ? '+' : ''}{Number(tx.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="textSecondary">
                        {tx.buy_price ? formatCurrency(Number(tx.buy_price)) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="primary">
                        {price > 0 ? formatCurrency(price) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(marketValue)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        if (!tx.date) return <Typography variant="body2">N/A</Typography>;
                        const d = new Date(tx.date);
                        if (isNaN(d.getTime())) return <Typography variant="body2">Invalid Date</Typography>;
                        // Format date as YYYY/MM/DD
                        const dateStr = `${d.getFullYear()}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getDate().toString().padStart(2,'0')}`;
                        // Format time as HH:mm:ss (24-hour)
                        const timeStr = `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}:${d.getSeconds().toString().padStart(2,'0')}`;
                        return (
                          <>
                            <Typography variant="body2" sx={{ lineHeight: 1 }}>{dateStr}</Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1 }}>{timeStr}</Typography>
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={walletAddress} arrow>
                        <span>{walletName}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {tx.tx_hash && (tx.tx_hash.startsWith('manual_') ? (
                        <Tooltip title={tx.tx_hash} arrow>
                          <Link
                            href="/manual-holdings"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <span style={{ fontFamily: 'monospace' }}>{shortHash}</span>
                            <OpenInNewIcon fontSize="small" sx={{ ml: 0.5 }} />
                          </Link>
                        </Tooltip>
                      ) : (
                        <Tooltip title={tx.tx_hash} arrow>
                          <Link
                            href={getExplorerUrl(tx.blockchain, tx.tx_hash)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            <span style={{ fontFamily: 'monospace' }}>{shortHash}</span>
                            <OpenInNewIcon fontSize="small" sx={{ ml: 0.5 }} />
                          </Link>
                        </Tooltip>
                      ))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              {realOnChainBalance !== null && realOnChainBalance !== undefined ? (
                <>
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                      Total Amount (On-Chain)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {realOnChainBalance > 0 ? '+' : ''}{Number(realOnChainBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Total Market Value (On-Chain)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {onChainMarketValue !== null ? formatCurrency(onChainMarketValue) : '-'}
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                  {missingAmount !== null && Math.abs(missingAmount) > 1e-8 && (
                    <TableRow>
                      <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                        Missing Transactions Amount
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: missingAmount > 0 ? 'success.main' : 'error.main' }}>
                        {missingAmount > 0 ? '+' : ''}{Number(missingAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                      </TableCell>
                      <TableCell colSpan={6}></TableCell>
                    </TableRow>
                  )}
                </>
              ) :
                <>
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                      Total Amount
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {totalAmount > 0 ? '+' : ''}{Number(totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      Total Market Value
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(totalMarketValue)}
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                </>
              }
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default TransactionsList; 